/**
 * Headless document schema: every node and mark a Nexus page can contain, with no
 * UI (no node views, no suggestions). Shared by the editor, the Markdown converter,
 * the indexer and tests, so they can never disagree about the document shape.
 */
import { getSchema, mergeAttributes, Node, type AnyExtension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { BulletList, ListItem, OrderedList, TaskItem, TaskList } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import Highlight from '@tiptap/extension-highlight';
import Code from '@tiptap/extension-code';
import Link from '@tiptap/extension-link';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';

/** `[[Target#heading|alias]]`. Points at a page id so renames never break it. */
export const WikiLink = Node.create({
  name: 'wikiLink',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      pageId: { default: null },
      /** Title as written; used when the page does not exist (yet). */
      target: { default: '' },
      heading: { default: null },
      alias: { default: null },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'a[data-wikilink]',
        getAttrs: (el) => ({
          pageId: el.getAttribute('data-page-id'),
          target: el.getAttribute('data-target') ?? el.textContent ?? '',
          heading: el.getAttribute('data-heading'),
          alias: el.getAttribute('data-alias'),
        }),
      },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    const { pageId, target, heading, alias } = node.attrs as WikiLinkAttrs;
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-wikilink': '',
        'data-page-id': pageId,
        'data-target': target,
        'data-heading': heading,
        'data-alias': alias,
        href: pageId ? `#/p/${pageId}` : '#',
      }),
      alias ?? (heading ? `${target} › ${heading}` : target),
    ];
  },
  renderText({ node }) {
    const { target, alias } = node.attrs as WikiLinkAttrs;
    return alias ?? target;
  },
});

export interface WikiLinkAttrs {
  pageId: string | null;
  target: string;
  heading: string | null;
  alias: string | null;
}

/** `![[Page]]` — shows another page (or a section of it) inline. */
export const Embed = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      pageId: { default: null },
      target: { default: '' },
      heading: { default: null },
      /** Display width of an image in pixels (`![[foto.png|300]]`). */
      width: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-embed]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-embed': '', 'data-page-id': node.attrs.pageId }),
      `![[${node.attrs.target as string}]]`,
    ];
  },
});

/** `#tag` or `#nested/tag`. */
export const Hashtag = Node.create({
  name: 'hashtag',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { tag: { default: '' } };
  },
  parseHTML() {
    return [{ tag: 'span[data-tag]', getAttrs: (el) => ({ tag: el.getAttribute('data-tag') }) }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const tag = node.attrs.tag as string;
    return ['span', mergeAttributes(HTMLAttributes, { 'data-tag': tag }), `#${tag}`];
  },
  renderText({ node }) {
    return `#${node.attrs.tag as string}`;
  },
});

/** Obsidian-style callout: `> [!INFO] Title`. `fold` is '+', '-' or null. */
export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() {
    return { kind: { default: 'note' }, title: { default: '' }, fold: { default: null } };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-callout]',
        getAttrs: (el) => ({
          kind: el.getAttribute('data-callout') || 'note',
          title: el.getAttribute('data-title') ?? '',
        }),
      },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-callout': node.attrs.kind,
        'data-title': node.attrs.title,
      }),
      0,
    ];
  },
});

/** Blocks side by side. In Markdown: `<!-- columns -->`, `<!-- column -->`, `<!-- /columns -->`. */
export const Columns = Node.create({
  name: 'columns',
  group: 'block',
  content: 'column{2,4}',
  defining: true,
  parseHTML() {
    return [{ tag: 'div[data-columns]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-columns': node.childCount,
        class: 'nx-columns',
        style: `--nx-cols: ${node.childCount}`,
      }),
      0,
    ];
  },
});

export const Column = Node.create({
  name: 'column',
  content: 'block+',
  isolating: true,
  parseHTML() {
    return [{ tag: 'div[data-column]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-column': '', class: 'nx-column' }), 0];
  },
});

/** Text and highlight colours offered in the editor (they read well in light and dark). */
export const TEXT_COLORS = [
  '#787774', // grijs
  '#9f6b53', // bruin
  '#d9730d', // oranje
  '#cb912f', // geel
  '#448361', // groen
  '#0f8a7e', // teal
  '#337ea9', // blauw
  '#9065b0', // paars
  '#c14c8a', // roze
  '#d44c47', // rood
];
export const HIGHLIGHT_COLORS = [
  'rgba(120, 119, 116, 0.2)',
  'rgba(159, 107, 83, 0.2)',
  'rgba(217, 115, 13, 0.2)',
  'rgba(255, 212, 0, 0.35)',
  'rgba(68, 131, 97, 0.22)',
  'rgba(15, 138, 126, 0.2)',
  'rgba(51, 126, 169, 0.2)',
  'rgba(144, 101, 176, 0.2)',
  'rgba(193, 76, 138, 0.2)',
  'rgba(212, 76, 71, 0.22)',
];

/**
 * Markdown the editor does not understand (HTML, footnotes, plugin syntax, …),
 * kept byte-for-byte so import → export never loses anything.
 */
export const RawBlock = Node.create({
  name: 'rawBlock',
  group: 'block',
  atom: true,
  addAttributes() {
    return { text: { default: '' } };
  },
  parseHTML() {
    return [{ tag: 'pre[data-raw]', getAttrs: (el) => ({ text: el.textContent ?? '' }) }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['pre', mergeAttributes(HTMLAttributes, { 'data-raw': '' }), node.attrs.text as string];
  },
});

export const RawInline = Node.create({
  name: 'rawInline',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { text: { default: '' } };
  },
  parseHTML() {
    return [{ tag: 'code[data-raw]', getAttrs: (el) => ({ text: el.textContent ?? '' }) }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['code', mergeAttributes(HTMLAttributes, { 'data-raw': '' }), node.attrs.text as string];
  },
  renderText({ node }) {
    return node.attrs.text as string;
  },
});

/** Inline code may combine with bold/italic (`*a `b` c*`), as in Markdown. */
const NexusCode = Code.extend({ excludes: '', priority: 40 }); // innermost: [`x`](url)

/** Lists remember tight/loose spacing and the bullet character, for faithful Markdown. */
const TightBulletList = BulletList.extend({
  addAttributes() {
    return { tight: { default: true }, bullet: { default: '-' } };
  },
});
const TightOrderedList = OrderedList.extend({
  addAttributes() {
    return { ...this.parent?.(), tight: { default: true } };
  },
});
const TightTaskList = TaskList.extend({
  addAttributes() {
    return { tight: { default: true }, bullet: { default: '-' } };
  },
});

/**
 * Task items keep the Obsidian Tasks status character: ' ' todo, 'x' done,
 * '/' in progress, '-' cancelled (any other character is preserved as-is).
 */
export const NexusTaskItem = TaskItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      // A new item made by pressing Enter starts open, with its own identity.
      status: {
        default: ' ',
        keepOnSplit: false,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-status') ?? ' ',
        renderHTML: (attrs: { status: string }) => ({ 'data-status': attrs.status }),
      },
      /** Identity of the task in the task database (not written to Markdown). */
      taskId: {
        default: null,
        keepOnSplit: false,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-task-id'),
        renderHTML: (attrs: { taskId: string | null }) =>
          attrs.taskId ? { 'data-task-id': attrs.taskId } : {},
      },
    };
  },
});

export interface SchemaOptions {
  /** Replace the plain code block (e.g. with a syntax-highlighting one). */
  codeBlock?: AnyExtension;
}

export function schemaExtensions(opts: SchemaOptions = {}): AnyExtension[] {
  return [
    StarterKit.configure({
      bulletList: false,
      orderedList: false,
      listItem: false,
      listKeymap: false,
      undoRedo: false, // history comes from Yjs
      code: false,
      link: false, // added last, see below
      codeBlock: opts.codeBlock ? false : {},
      heading: { levels: [1, 2, 3, 4, 5, 6] },
    }),
    ...(opts.codeBlock ? [opts.codeBlock] : []),
    TightBulletList,
    TightOrderedList,
    ListItem,
    TightTaskList,
    NexusTaskItem.configure({ nested: true }),
    TableKit.configure({ table: { resizable: true } }),
    Image.configure({ inline: true }),
    // Markdown: `==x==`; with a colour `<mark style="background: …">`.
    Highlight.configure({ multicolor: true }),
    // Markdown: `<span style="color: …">`, `<u>`, `<p align="center">`.
    TextStyle,
    Color,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    NexusCode,
    // Ranked after the formatting marks (TipTap defaults to first): `*a [b](c) d*`
    // keeps the emphasis around the link instead of splitting it.
    Link.extend({ priority: 50 }).configure({ openOnClick: false, autolink: true }),
    WikiLink,
    Embed,
    Hashtag,
    Callout,
    Columns,
    Column,
    RawBlock,
    RawInline,
  ];
}

let cached: ReturnType<typeof getSchema> | null = null;

/** The ProseMirror schema for page documents. */
export function nexusSchema() {
  return (cached ??= getSchema(schemaExtensions()));
}
